import React, { useCallback, useEffect, useState } from 'react'
import { USER_ROLES } from '../config/security'
import { supabase } from '../supabaseClient'
import {
  carregarWhatsAppConfig,
  carregarWebhookPayload,
  enviarMensagemWhatsApp,
  filtrarTemplatesIniciarConversa,
  getWhatsAppWebhookCallbackUrl,
  listarParametrosWhatsApp,
  listarTemplatesWhatsApp,
  listarWebhookEventos,
  salvarParametroWhatsApp,
  salvarWhatsAppVerifyToken
} from '../services/whatsappApi'
import { listarVendedoresEspecialistas, TEXTO_MENSAGEM_INICIAL_WHATSAPP } from '../services/whatsappCrmApi'
import {
  adicionarLiberadoProposta,
  formatarTelefoneLiberado,
  listarLiberadosProposta,
  removerLiberadoProposta
} from '../utils/whatsappPropostaLiberados'
import {
  carregarMenuWhatsApp,
  listarUsuariosMenuWhatsApp,
  salvarMenuWhatsApp
} from '../utils/whatsappMenuCanais'
import WhatsAppCrm from './WhatsAppCrm'
import './WhatsApp.css'

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '')
}

/** Chave única para identificar um template (ID da Meta, ou nome + idioma). */
function templateKey(tpl) {
  if (tpl?.id) return `id:${tpl.id}`
  const lang =
    typeof tpl?.language === 'string'
      ? tpl.language
      : (tpl?.language?.code || '')
  return `${tpl?.name || ''}::${lang}`
}

/**
 * Extrai o texto do corpo (BODY) e a quantidade de variáveis {{n}} de um template da Meta.
 */
function analisarCorpoTemplate(components) {
  const body = (components || []).find(
    (c) => String(c?.type || '').toUpperCase() === 'BODY'
  )
  const texto = body?.text || ''
  const matches = String(texto).match(/\{\{\s*\d+\s*\}\}/g) || []
  const numeros = matches.map((m) => parseInt(m.replace(/[^\d]/g, ''), 10))
  const quantidade = numeros.length ? Math.max(...numeros) : 0
  return { texto, quantidade }
}

function languageTemplate(tpl) {
  return typeof tpl?.language === 'string'
    ? tpl.language
    : (tpl?.language?.code || '—')
}

function labelTemplate(tpl) {
  const lang = languageTemplate(tpl)
  const status = tpl?.status ? ` — ${tpl.status}` : ''
  const id = tpl?.id ? ` · ID ${tpl.id}` : ''
  return `${tpl?.name || 'sem nome'} (${lang})${status}${id}`
}

function gerarTokenAleatorio() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let out = ''
  for (let i = 0; i < 32; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

const CAMPOS_WEBHOOK_META = [
  { field: 'messages', descricao: 'Mensagens recebidas e status de mensagens enviadas', essencial: true },
  { field: 'message_template_status_update', descricao: 'Alterações no status de templates', essencial: false },
  { field: 'message_template_quality_update', descricao: 'Pontuação de qualidade de templates', essencial: false },
  { field: 'account_alerts', descricao: 'Limites de mensagens, perfil e status da conta', essencial: false },
  { field: 'account_update', descricao: 'Alterações na conta WhatsApp Business', essencial: false },
  { field: 'phone_number_quality_update', descricao: 'Nível de taxa de transferência do número', essencial: false }
]

/**
 * Menu API Whatsapp — CRM, webhooks e testes da WhatsApp Cloud API (Meta).
 */
const WhatsApp = ({ onBack, userRole, userId, chatZap, whatsappGestor, whatsappProposta }) => {
  const isAdmin = userRole === USER_ROLES.ADMINISTRADOR
  const isEspecialista = userRole === USER_ROLES.ESPECIALISTA
  const [chatZapDb, setChatZapDb] = useState(Number(chatZap) === 1)
  const [gestorDb, setGestorDb] = useState(Number(whatsappGestor) === 1)
  const [propostaDb, setPropostaDb] = useState(Number(whatsappProposta) === 1)
  const chatZapLiberado = Number(chatZap) === 1 || chatZapDb === true
  const isGestor = Number(whatsappGestor) === 1 || gestorDb === true
  const podeAbaProposta = Number(whatsappProposta) === 1 || propostaDb === true
  const atendeZap = isEspecialista || chatZapLiberado
  const podeAcessar = isAdmin || isGestor || atendeZap || podeAbaProposta

  const [abaAtiva, setAbaAtiva] = useState('crm')

  const [telefone, setTelefone] = useState('')
  const [tipoEnvio, setTipoEnvio] = useState(isAdmin ? 'text' : 'template')
  const [mensagem, setMensagem] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [templateLanguage, setTemplateLanguage] = useState('pt_BR')
  const [wabaIdListagem, setWabaIdListagem] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  // Templates cadastrados na Meta
  const [templatesMeta, setTemplatesMeta] = useState([])
  const [carregandoTemplates, setCarregandoTemplates] = useState(false)
  const [erroTemplates, setErroTemplates] = useState(null)
  const [avisoTemplates, setAvisoTemplates] = useState(null)
  const [templateSelecionadoKey, setTemplateSelecionadoKey] = useState('')
  const [variaveisTemplate, setVariaveisTemplate] = useState([])
  const [vendedores, setVendedores] = useState([])
  const [vendedorSelecionadoId, setVendedorSelecionadoId] = useState('')
  const [parametrosTemplate, setParametrosTemplate] = useState([])
  const [parametroSelecionadoId, setParametroSelecionadoId] = useState('')
  const [salvandoParametro, setSalvandoParametro] = useState(false)
  const [feedbackParametro, setFeedbackParametro] = useState(null)

  const [verifyToken, setVerifyToken] = useState('')
  const [configSalvaEm, setConfigSalvaEm] = useState(null)
  const [salvandoToken, setSalvandoToken] = useState(false)
  const [feedbackConfig, setFeedbackConfig] = useState(null)
  const [copiado, setCopiado] = useState(null)

  const [eventos, setEventos] = useState([])
  const [carregandoEventos, setCarregandoEventos] = useState(false)
  const [eventoSelecionado, setEventoSelecionado] = useState(null)
  const [payloadDetalhe, setPayloadDetalhe] = useState(null)

  const [conversaIniciadaId, setConversaIniciadaId] = useState(null)
  const [crmRefreshNonce, setCrmRefreshNonce] = useState(0)
  const [modalIniciarAberto, setModalIniciarAberto] = useState(false)
  const [liberadosProposta, setLiberadosProposta] = useState([])
  const [novoLiberadoTel, setNovoLiberadoTel] = useState('')
  const [novoLiberadoNome, setNovoLiberadoNome] = useState('')
  const [salvandoLiberado, setSalvandoLiberado] = useState(false)
  const [feedbackLiberado, setFeedbackLiberado] = useState(null)
  const [menuAtivo, setMenuAtivo] = useState(true)
  const [menuMensagem, setMenuMensagem] = useState('')
  const [menuCanais, setMenuCanais] = useState([])
  const [menuUsuarios, setMenuUsuarios] = useState([])
  const [salvandoMenu, setSalvandoMenu] = useState(false)
  const [feedbackMenu, setFeedbackMenu] = useState(null)

  const callbackUrl = getWhatsAppWebhookCallbackUrl()

  const carregarConfig = useCallback(async () => {
    const res = await carregarWhatsAppConfig()
    if (res.success) {
      setVerifyToken(res.verify_token || '')
      setConfigSalvaEm(res.updated_at)
    }
  }, [])

  const carregarEventos = useCallback(async () => {
    setCarregandoEventos(true)
    try {
      const res = await listarWebhookEventos({ limit: 50 })
      if (res.success) setEventos(res.eventos)
    } finally {
      setCarregandoEventos(false)
    }
  }, [])

  const carregarLiberadosProposta = useCallback(async () => {
    const res = await listarLiberadosProposta()
    if (res.success) setLiberadosProposta(res.itens)
  }, [])

  const carregarMenuCanais = useCallback(async () => {
    const [menu, usuarios] = await Promise.all([carregarMenuWhatsApp(), listarUsuariosMenuWhatsApp()])
    if (menu.success) {
      setMenuAtivo(Boolean(menu.config?.ativo))
      setMenuMensagem(menu.config?.mensagem_boas_vindas || '')
      setMenuCanais(menu.canais || [])
    }
    if (usuarios.success) setMenuUsuarios(usuarios.usuarios || [])
  }, [])

  const aplicarTemplate = (tpl) => {
    if (!tpl) {
      setTemplateSelecionadoKey('')
      setTemplateName('')
      setVariaveisTemplate([])
      return
    }
    const lang =
      typeof tpl.language === 'string'
        ? tpl.language
        : (tpl.language?.code || '')
    setTemplateSelecionadoKey(templateKey(tpl))
    setTemplateName(tpl.name || '')
    if (tpl.id) setTemplateId(String(tpl.id))
    setTemplateLanguage(lang)
    const { quantidade } = analisarCorpoTemplate(tpl.components)
    setVariaveisTemplate(Array.from({ length: quantidade }, () => ''))
  }

  const carregarParametros = useCallback(async () => {
    const res = await listarParametrosWhatsApp()
    if (res.success) {
      const lista = filtrarTemplatesIniciarConversa(res.parametros || [])
      setParametrosTemplate(lista)
      return lista
    }
    if (res.error) {
      setFeedbackParametro({ type: 'error', text: res.error })
    }
    return []
  }, [])

  const carregarTemplates = useCallback(async (idInformado) => {
    const id = String(idInformado ?? templateId ?? '').trim()
    if (!id) {
      setErroTemplates('Informe o ID do template (ou da WABA) e clique em Carregar.')
      return
    }
    setCarregandoTemplates(true)
    setErroTemplates(null)
    setAvisoTemplates(null)
    setResultado(null)
    try {
      const res = await listarTemplatesWhatsApp({ usuario_id: userId, template_id: id })
      if (res.success) {
        const lista = filtrarTemplatesIniciarConversa(res.templates || [])
        setTemplatesMeta(lista)
        setWabaIdListagem(res.waba_id || '')
        if (res.warning) setAvisoTemplates(res.warning)
        const preferido = lista.find((t) => String(t.id) === String(id)) || (lista.length === 1 ? lista[0] : null)
        if (preferido) {
          aplicarTemplate(preferido)
        } else {
          aplicarTemplate(null)
        }
        if (lista.length === 0) {
          setErroTemplates('Nenhum template disponível para iniciar conversa.')
        }
      } else {
        setTemplatesMeta([])
        setWabaIdListagem(res.waba_id || '')
        setTemplateSelecionadoKey('')
        setTemplateName('')
        setVariaveisTemplate([])
        setErroTemplates(res.error || 'Não foi possível carregar os templates.')
      }
    } finally {
      setCarregandoTemplates(false)
    }
  }, [userId])

  const carregarTemplateDoUsuario = useCallback(async () => {
    if (!userId) {
      setErroTemplates('Usuário não identificado.')
      aplicarTemplate(null)
      return
    }
    const { data, error } = await supabase
      .from('usuarios_sistema')
      .select('whatsapp_template_id, whatsapp_template_nome, whatsapp_template_language')
      .eq('id', userId)
      .maybeSingle()
    if (error || !data?.whatsapp_template_id) {
      setParametroSelecionadoId('')
      setTemplatesMeta([])
      aplicarTemplate(null)
      setErroTemplates(
        'Nenhum template atribuído ao seu usuário. Peça ao administrador para definir em Gerenciamento de Usuários.'
      )
      return
    }
    setErroTemplates(null)
    setTemplateId(data.whatsapp_template_id)
    setTemplateName(data.whatsapp_template_nome || '')
    setTemplateLanguage(data.whatsapp_template_language || 'pt_BR')
    await carregarTemplates(data.whatsapp_template_id)
  }, [userId, carregarTemplates])

  useEffect(() => {
    if (isAdmin) {
      carregarConfig()
      carregarEventos()
      carregarMenuCanais()
    }
    if (podeAbaProposta) {
      carregarLiberadosProposta()
    }
  }, [isAdmin, podeAbaProposta, carregarConfig, carregarEventos, carregarLiberadosProposta, carregarMenuCanais])

  useEffect(() => {
    if (podeAbaProposta && !isAdmin && !isGestor && !atendeZap) {
      setAbaAtiva('proposta')
    }
  }, [podeAbaProposta, isAdmin, isGestor, atendeZap])

  useEffect(() => {
    if (isAdmin && modalIniciarAberto && vendedores.length === 0) {
      listarVendedoresEspecialistas().then((res) => {
        if (res.success) setVendedores(res.vendedores || [])
      })
    }
  }, [isAdmin, modalIniciarAberto, vendedores.length])

  useEffect(() => {
    if (!modalIniciarAberto) return undefined
    setTipoEnvio('template')
    let cancelado = false
    ;(async () => {
      await carregarTemplateDoUsuario()
      if (cancelado) return
    })()
    return () => {
      cancelado = true
    }
  }, [modalIniciarAberto, carregarTemplateDoUsuario])

  useEffect(() => {
    if (!userId) return undefined
    let cancelado = false
    supabase
      .from('usuarios_sistema')
      .select('chat_zap, whatsapp_gestor, whatsapp_proposta')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelado) return
        setChatZapDb(Number(data?.chat_zap) === 1)
        setGestorDb(Number(data?.whatsapp_gestor) === 1)
        setPropostaDb(Number(data?.whatsapp_proposta) === 1)
      })
    return () => {
      cancelado = true
    }
  }, [userId])

  const templateSelecionado = templatesMeta.find((t) => templateKey(t) === templateSelecionadoKey) || null
  const infoCorpoTemplate = templateSelecionado ? analisarCorpoTemplate(templateSelecionado.components) : { texto: '', quantidade: 0 }

  const handleSelecionarTemplate = (key) => {
    const tpl = templatesMeta.find((t) => templateKey(t) === key) || null
    aplicarTemplate(tpl)
    setResultado(null)
  }

  const handleSelecionarParametro = (rowId) => {
    setParametroSelecionadoId(rowId)
    setFeedbackParametro(null)
    const param = parametrosTemplate.find((p) => p.id === rowId)
    if (!param) return
    setTemplateId(param.template_id)
    carregarTemplates(param.template_id)
  }

  const handleAdicionarParametro = async () => {
    const id = String(templateId || '').trim()
    if (!id) {
      setFeedbackParametro({ type: 'error', text: 'Informe o ID para salvar como parâmetro.' })
      return
    }
    setSalvandoParametro(true)
    setFeedbackParametro(null)
    try {
      const lang = String(templateLanguage || '').trim()
      const res = await salvarParametroWhatsApp({
        template_id: id,
        nome: templateName || templateSelecionado?.name || '',
        language: lang && lang !== '—' ? lang : ''
      })
      if (!res.success) {
        setFeedbackParametro({ type: 'error', text: res.error })
        return
      }
      setFeedbackParametro({ type: 'success', text: res.message })
      await carregarParametros()
      if (res.parametro?.id) setParametroSelecionadoId(res.parametro.id)
    } finally {
      setSalvandoParametro(false)
    }
  }

  const handleVariavelChange = (index, value) => {
    setVariaveisTemplate((prev) => {
      const novo = [...prev]
      novo[index] = value
      return novo
    })
  }

  const abrirModalIniciar = () => {
    setTelefone('')
    setMensagem('')
    setResultado(null)
    setTipoEnvio('template')
    setModalIniciarAberto(true)
  }

  const fecharModalIniciar = () => {
    if (enviando) return
    setModalIniciarAberto(false)
    setResultado(null)
  }

  if (!podeAcessar) {
    return (
      <div className="whatsapp-container">
        <div className="whatsapp-header">
          <h1 className="whatsapp-title">{atendeZap ? 'WhatsApp CRM' : 'API Whatsapp'}</h1>
          <button type="button" onClick={onBack} className="whatsapp-btn-back">
            ← Voltar
          </button>
        </div>
        <div className="whatsapp-access-denied">
          <h2>Acesso negado</h2>
          <p>Esta tela é exclusiva para quem está liberado no Chat Zap.</p>
        </div>
      </div>
    )
  }

  const copiarTexto = async (texto, chave) => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(chave)
      setTimeout(() => setCopiado(null), 2000)
    } catch {
      setFeedbackConfig({ type: 'error', text: 'Não foi possível copiar. Selecione e copie manualmente.' })
    }
  }

  const handleSalvarToken = async (e) => {
    e.preventDefault()
    setFeedbackConfig(null)
    setSalvandoToken(true)
    try {
      const res = await salvarWhatsAppVerifyToken(verifyToken)
      if (!res.success) {
        setFeedbackConfig({ type: 'error', text: res.error })
        return
      }
      setFeedbackConfig({ type: 'success', text: res.message })
      await carregarConfig()
    } finally {
      setSalvandoToken(false)
    }
  }

  const handleAdicionarLiberado = async (e) => {
    e.preventDefault()
    setFeedbackLiberado(null)
    setSalvandoLiberado(true)
    try {
      const res = await adicionarLiberadoProposta({
        telefone: novoLiberadoTel,
        nome: novoLiberadoNome
      })
      if (!res.success) {
        setFeedbackLiberado({ type: 'error', text: res.error })
        return
      }
      setNovoLiberadoTel('')
      setNovoLiberadoNome('')
      setFeedbackLiberado({ type: 'success', text: 'Número liberado para gerar proposta.' })
      await carregarLiberadosProposta()
    } finally {
      setSalvandoLiberado(false)
    }
  }

  const handleRemoverLiberado = async (id) => {
    setFeedbackLiberado(null)
    const res = await removerLiberadoProposta(id)
    if (!res.success) {
      setFeedbackLiberado({ type: 'error', text: res.error })
      return
    }
    await carregarLiberadosProposta()
  }

  const handleSalvarMenu = async (e) => {
    e.preventDefault()
    setFeedbackMenu(null)
    setSalvandoMenu(true)
    try {
      const res = await salvarMenuWhatsApp({
        ativo: menuAtivo,
        mensagem: menuMensagem,
        canais: menuCanais
      })
      if (!res.success) {
        setFeedbackMenu({ type: 'error', text: res.error })
        return
      }
      setFeedbackMenu({ type: 'success', text: 'Menu de boas-vindas salvo.' })
      await carregarMenuCanais()
    } finally {
      setSalvandoMenu(false)
    }
  }

  const handleVerPayload = async (eventoId) => {
    if (eventoSelecionado === eventoId) {
      setEventoSelecionado(null)
      setPayloadDetalhe(null)
      return
    }
    setEventoSelecionado(eventoId)
    const res = await carregarWebhookPayload(eventoId)
    if (res.success) {
      setPayloadDetalhe(res.payload)
    } else {
      setPayloadDetalhe({ error: res.error })
    }
  }

  const handleSubmitEnvio = async (e) => {
    e.preventDefault()
    setResultado(null)

    const digits = onlyDigits(telefone)
    if (digits.length < 10) {
      setResultado({ type: 'error', text: 'Informe um telefone válido (DDD + número).' })
      return
    }

    if (tipoEnvio === 'text' && !mensagem.trim()) {
      setResultado({ type: 'error', text: 'Informe a mensagem.' })
      return
    }

    let templateComponents
    let bodyPreview
    if (tipoEnvio === 'template') {
      if (!String(templateId || '').trim()) {
        setResultado({ type: 'error', text: 'Informe o ID do template.' })
        return
      }
      if (!templateSelecionado) {
        setResultado({ type: 'error', text: 'Carregue o template pelo ID antes de enviar.' })
        return
      }
      if (variaveisTemplate.length > 0) {
        if (variaveisTemplate.some((v) => !String(v || '').trim())) {
          setResultado({ type: 'error', text: 'Preencha todas as variáveis do template.' })
          return
        }
        templateComponents = [
          {
            type: 'body',
            parameters: variaveisTemplate.map((v) => ({ type: 'text', text: String(v).trim() }))
          }
        ]
      }
      // Texto simples no CRM: o cliente recebe o template na íntegra no WhatsApp.
      bodyPreview = TEXTO_MENSAGEM_INICIAL_WHATSAPP
    }

    setEnviando(true)
    try {
      const res = await enviarMensagemWhatsApp({
        usuario_id: userId,
        to: digits,
        type: tipoEnvio,
        message: mensagem.trim(),
        template_id: String(templateId || '').trim() || undefined,
        template_name: templateName.trim(),
        template_language: templateLanguage.trim(),
        template_components: templateComponents,
        body_preview: bodyPreview,
        vendedor_id: tipoEnvio === 'template'
          ? (isAdmin && vendedorSelecionadoId ? vendedorSelecionadoId : userId)
          : undefined
      })

      if (!res.success) {
        setResultado({ type: 'error', text: res.error, details: res.details })
        return
      }

      setResultado({
        type: 'success',
        text: res.message,
        messageId: res.message_id,
        response: res.response
      })
      if (tipoEnvio === 'template') {
        if (res.conversa_id) setConversaIniciadaId(res.conversa_id)
        setCrmRefreshNonce((n) => n + 1)
        setAbaAtiva('crm')
        setModalIniciarAberto(false)
        setTelefone('')
        setResultado(null)
      }
    } finally {
      setEnviando(false)
    }
  }

  const modoCrm = abaAtiva === 'crm'

  return (
    <>
    <div className={modoCrm ? 'whatsapp-shell-crm' : 'whatsapp-container whatsapp-container-wide'}>
      <div className="whatsapp-header">
        <h1 className="whatsapp-title">{atendeZap && !isGestor ? 'WhatsApp CRM' : 'API Whatsapp'}</h1>
        <button type="button" onClick={onBack} className="whatsapp-btn-back">
          ← Voltar
        </button>
      </div>

      {!modoCrm && (
        <p className="whatsapp-intro">
          {atendeZap && !isGestor
            ? 'Atenda seus clientes pelo WhatsApp em tempo real.'
            : (
              <>
                CRM de atendimento, configuração de webhooks e testes da <strong>WhatsApp Cloud API</strong> (Meta).
              </>
            )}
        </p>
      )}

      {(isAdmin || podeAbaProposta) && (
        <div className="whatsapp-tabs">
          {(isAdmin || isGestor || atendeZap) && (
            <button
              type="button"
              className={`whatsapp-tab ${abaAtiva === 'crm' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('crm')}
            >
              CRM
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className={`whatsapp-tab ${abaAtiva === 'menu' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('menu')}
            >
              Menu
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className={`whatsapp-tab ${abaAtiva === 'webhooks' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('webhooks')}
            >
              Webhooks
            </button>
          )}
          {podeAbaProposta && (
            <button
              type="button"
              className={`whatsapp-tab ${abaAtiva === 'proposta' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('proposta')}
            >
              Proposta
            </button>
          )}
        </div>
      )}

      {abaAtiva === 'crm' && (
        <div className="container-fluid p-0 wa-crm-host">
          <WhatsAppCrm
            userRole={userRole}
            userId={userId}
            chatZap={chatZapLiberado ? 1 : 0}
            whatsappGestor={isGestor ? 1 : 0}
            whatsappProposta={podeAbaProposta ? 1 : 0}
            conversaInicialId={conversaIniciadaId}
            refreshNonce={crmRefreshNonce}
            onIniciarConversa={abrirModalIniciar}
          />
        </div>
      )}

      {podeAbaProposta && abaAtiva === 'proposta' && (
        <div className="whatsapp-webhooks">
          <section className="whatsapp-card">
            <h2 className="whatsapp-card-title">Quem pode gerar proposta pelo WhatsApp</h2>
            <p className="whatsapp-card-desc">
              Só os números desta lista conseguem pedir o PDF automático (escrevendo <strong>gerar proposta</strong>).
              Sem cadastro aqui, o bot não inicia o fluxo.
            </p>
            <div className="whatsapp-alert whatsapp-alert-info">
              Execute a migration <code>database/164_whatsapp_proposta_liberados.sql</code> no Supabase
              e publique a Edge Function <code>whatsapp-webhook</code> para valer no Zap.
            </div>

            <form onSubmit={handleAdicionarLiberado} className="whatsapp-liberados-form">
              <input
                type="text"
                className="form-control"
                placeholder="Nome (opcional)"
                value={novoLiberadoNome}
                onChange={(e) => setNovoLiberadoNome(e.target.value)}
                disabled={salvandoLiberado}
              />
              <input
                type="tel"
                className="form-control"
                placeholder="WhatsApp com DDD"
                value={novoLiberadoTel}
                onChange={(e) => setNovoLiberadoTel(e.target.value)}
                disabled={salvandoLiberado}
                required
              />
              <button type="submit" className="btn btn-success" disabled={salvandoLiberado || !novoLiberadoTel.trim()}>
                {salvandoLiberado ? 'Salvando...' : 'Liberar'}
              </button>
            </form>

            {feedbackLiberado && (
              <div className={`whatsapp-result whatsapp-result-${feedbackLiberado.type}`}>
                {feedbackLiberado.text}
              </div>
            )}

            {liberadosProposta.length === 0 ? (
              <p className="text-muted whatsapp-empty">Nenhum número liberado. Ninguém consegue gerar proposta pelo Zap.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm whatsapp-fields-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>WhatsApp</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {liberadosProposta.map((item) => (
                      <tr key={item.id}>
                        <td>{item.nome || '—'}</td>
                        <td>{formatarTelefoneLiberado(item.telefone)}</td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoverLiberado(item.id)}
                          >
                            Remover
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {isAdmin && abaAtiva === 'menu' && (
        <div className="whatsapp-webhooks">
          <section className="whatsapp-card">
            <h2 className="whatsapp-card-title">Menu de boas-vindas</h2>
            <p className="whatsapp-card-desc">
              Quando um cliente novo manda a primeira mensagem, o bot responde com este texto e as opções.
              Cada opção encaminha a conversa para o usuário escolhido.
            </p>
            <div className="whatsapp-alert whatsapp-alert-info">
              Execute <code>database/166_whatsapp_menu_canais.sql</code> no Supabase e publique a função{' '}
              <code>whatsapp-webhook</code>. O atendente precisa estar liberado no Chat Zap para ver a conversa.
            </div>

            <form onSubmit={handleSalvarMenu}>
              <div className="checkbox-item" style={{ marginBottom: 16 }}>
                <input
                  type="checkbox"
                  id="whatsapp_menu_ativo"
                  checked={menuAtivo}
                  onChange={(e) => setMenuAtivo(e.target.checked)}
                />
                <label htmlFor="whatsapp_menu_ativo">Ativar menu de boas-vindas</label>
              </div>

              <div className="whatsapp-config-row">
                <label htmlFor="whatsapp_menu_msg" className="form-label">Mensagem de boas-vindas</label>
                <textarea
                  id="whatsapp_menu_msg"
                  className="form-control"
                  rows={7}
                  value={menuMensagem}
                  onChange={(e) => setMenuMensagem(e.target.value)}
                  disabled={salvandoMenu}
                />
              </div>

              {menuCanais.map((canal) => (
                <div key={canal.codigo} className="whatsapp-config-row">
                  <label className="form-label" htmlFor={`menu_canal_${canal.codigo}`}>
                    {canal.opcao} — {canal.titulo}
                  </label>
                  <select
                    id={`menu_canal_${canal.codigo}`}
                    className="form-select"
                    value={canal.usuario_id || ''}
                    onChange={(e) => {
                      const usuario_id = e.target.value || null
                      setMenuCanais((prev) => prev.map((c) => (
                        c.codigo === canal.codigo ? { ...c, usuario_id } : c
                      )))
                    }}
                    disabled={salvandoMenu}
                  >
                    <option value="">Selecione o usuário</option>
                    {menuUsuarios.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.nome}{Number(u.chat_zap) === 1 ? '' : ' (sem Chat Zap)'}
                      </option>
                    ))}
                  </select>
                </div>
              ))}

              <button type="submit" className="btn btn-success" disabled={salvandoMenu}>
                {salvandoMenu ? 'Salvando...' : 'Salvar menu'}
              </button>
            </form>

            {feedbackMenu && (
              <div className={`whatsapp-result whatsapp-result-${feedbackMenu.type}`}>
                {feedbackMenu.text}
              </div>
            )}
          </section>
        </div>
      )}

      {isAdmin && abaAtiva === 'webhooks' && (
        <div className="whatsapp-webhooks">
          <section className="whatsapp-card">
            <h2 className="whatsapp-card-title">1. Configurar no Meta Developer</h2>
            <p className="whatsapp-card-desc">
              No painel <strong>Meta for Developers → seu app → WhatsApp → Configuração</strong>,
              preencha os campos abaixo e clique em <strong>Verificar e salvar</strong>.
            </p>

            <div className="whatsapp-config-row">
              <label className="form-label">URL de callback</label>
              <div className="whatsapp-copy-row">
                <code className="whatsapp-code-block">{callbackUrl}</code>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => copiarTexto(callbackUrl, 'url')}
                >
                  {copiado === 'url' ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            </div>

            <form onSubmit={handleSalvarToken} className="whatsapp-token-form">
              <div className="whatsapp-config-row">
                <label htmlFor="whatsapp-verify-token" className="form-label">
                  Verificar token *
                </label>
                <div className="whatsapp-copy-row">
                  <input
                    id="whatsapp-verify-token"
                    type="text"
                    className="form-control"
                    placeholder="Token secreto (mesmo valor no Meta e aqui)"
                    value={verifyToken}
                    onChange={(e) => setVerifyToken(e.target.value)}
                    disabled={salvandoToken}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setVerifyToken(gerarTokenAleatorio())}
                    disabled={salvandoToken}
                  >
                    Gerar
                  </button>
                </div>
                <small className="text-muted">
                  Salve aqui e use o <strong>mesmo valor</strong> no campo &quot;Verificar token&quot; do Meta.
                  {configSalvaEm && (
                    <> Última atualização: {new Date(configSalvaEm).toLocaleString('pt-BR')}.</>
                  )}
                </small>
              </div>

              <button type="submit" className="btn btn-success" disabled={salvandoToken}>
                {salvandoToken ? 'Salvando...' : 'Salvar token'}
              </button>
            </form>

            {feedbackConfig && (
              <div className={`whatsapp-result whatsapp-result-${feedbackConfig.type}`}>
                {feedbackConfig.text}
              </div>
            )}

            <div className="whatsapp-alert whatsapp-alert-info">
              <strong>Antes de verificar:</strong> execute a migration{' '}
              <code>database/136_create_whatsapp_webhooks.sql</code> no Supabase e faça deploy da Edge Function{' '}
              <code>whatsapp-webhook</code>.
            </div>
          </section>

          <section className="whatsapp-card">
            <h2 className="whatsapp-card-title">2. Assinar campos de webhook</h2>
            <p className="whatsapp-card-desc">
              No mesmo painel, na seção <strong>Campos do webhook</strong>, assine os eventos desejados.
              Para testes iniciais, o campo <code>messages</code> é essencial.
            </p>
            <div className="table-responsive">
              <table className="table table-sm whatsapp-fields-table">
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  {CAMPOS_WEBHOOK_META.map((item) => (
                    <tr key={item.field}>
                      <td>
                        <code>{item.field}</code>
                        {item.essencial && <span className="whatsapp-badge-essencial">recomendado</span>}
                      </td>
                      <td>{item.descricao}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="whatsapp-card">
            <div className="whatsapp-card-header-row">
              <h2 className="whatsapp-card-title">3. Eventos recebidos</h2>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={carregarEventos}
                disabled={carregandoEventos}
              >
                {carregandoEventos ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>
            <p className="whatsapp-card-desc">
              Webhooks entregues pela Meta aparecem aqui após a verificação. Use o botão de teste no painel Meta
              para enviar uma carga de exemplo.
            </p>

            {eventos.length === 0 ? (
              <p className="text-muted whatsapp-empty">Nenhum evento recebido ainda.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm whatsapp-events-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Campo</th>
                      <th>Tipo</th>
                      <th>Contato</th>
                      <th>Conteúdo / Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventos.map((ev) => (
                      <React.Fragment key={ev.id}>
                        <tr>
                          <td>{new Date(ev.received_at).toLocaleString('pt-BR')}</td>
                          <td><code>{ev.field || '—'}</code></td>
                          <td>{ev.message_type || '—'}</td>
                          <td>
                            {ev.contact_name || ev.wa_id || '—'}
                            {ev.wa_id && ev.contact_name && (
                              <small className="d-block text-muted">{ev.wa_id}</small>
                            )}
                          </td>
                          <td>{ev.message_body || ev.status || '—'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => handleVerPayload(ev.id)}
                            >
                              {eventoSelecionado === ev.id ? 'Ocultar JSON' : 'Ver JSON'}
                            </button>
                          </td>
                        </tr>
                        {eventoSelecionado === ev.id && payloadDetalhe && (
                          <tr>
                            <td colSpan={6}>
                              <pre className="whatsapp-result-json">
                                {JSON.stringify(payloadDetalhe, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>

      {modalIniciarAberto && (
        <div className="wa-iniciar-overlay" onClick={fecharModalIniciar} role="presentation">
          <div
            className="wa-iniciar-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wa-iniciar-titulo"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="wa-iniciar-modal-header">
              <h2 id="wa-iniciar-titulo" className="wa-iniciar-modal-title">Iniciar conversa</h2>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={fecharModalIniciar}
                disabled={enviando}
              />
            </div>
            <p className="wa-iniciar-modal-desc">
              Informe o telefone. A mensagem inicial será enviada automaticamente.
            </p>
            <form onSubmit={handleSubmitEnvio}>
              <div className="whatsapp-field">
                <label htmlFor="whatsapp-telefone" className="form-label">
                  Telefone destino *
                </label>
                <input
                  id="whatsapp-telefone"
                  type="tel"
                  className="form-control"
                  placeholder="(11) 99999-9999"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  disabled={enviando}
                  autoFocus
                />
                <small className="text-muted">Somente números. O DDI 55 é adicionado automaticamente se omitido.</small>
              </div>

              {isAdmin && (
                <details className="wa-iniciar-admin">
                  <summary>Opções do administrador</summary>
                  <div className="whatsapp-field mt-3">
                    <label htmlFor="whatsapp-vendedor" className="form-label">
                      Atribuir conversa a
                    </label>
                    <select
                      id="whatsapp-vendedor"
                      className="form-control"
                      value={vendedorSelecionadoId}
                      onChange={(e) => setVendedorSelecionadoId(e.target.value)}
                      disabled={enviando}
                    >
                      <option value="">Eu mesmo (quem envia)</option>
                      {vendedores.map((v) => (
                        <option key={v.id} value={v.id}>{v.nome}</option>
                      ))}
                    </select>
                  </div>
                </details>
              )}

              {erroTemplates && (
                <small className="text-danger d-block mb-2">{erroTemplates}</small>
              )}

              {carregandoTemplates && (
                <small className="text-muted d-block mb-2">Carregando template...</small>
              )}

              {templateSelecionado && (
                <div className="whatsapp-field">
                  <small className="text-muted d-block">
                    Template: <strong>{templateSelecionado.name || '—'}</strong>
                    {' · '}
                    {languageTemplate(templateSelecionado)}
                    {templateSelecionado.status ? ` · ${templateSelecionado.status}` : ''}
                  </small>
                </div>
              )}

              {templateSelecionado && infoCorpoTemplate.texto && (
                <div className="whatsapp-field">
                  <span className="form-label d-block">Prévia</span>
                  <div className="whatsapp-template-preview">{infoCorpoTemplate.texto}</div>
                </div>
              )}

              {variaveisTemplate.map((valor, index) => (
                <div className="whatsapp-field" key={`var-${index}`}>
                  <label htmlFor={`whatsapp-var-${index}`} className="form-label">
                    Variável {`{{${index + 1}}}`} *
                  </label>
                  <input
                    id={`whatsapp-var-${index}`}
                    type="text"
                    className="form-control"
                    value={valor}
                    onChange={(e) => handleVariavelChange(index, e.target.value)}
                    placeholder={`Valor para {{${index + 1}}}`}
                    disabled={enviando}
                  />
                </div>
              ))}

              {resultado && (
                <div className={`whatsapp-result whatsapp-result-${resultado.type}`}>
                  <strong>{resultado.type === 'success' ? 'Sucesso' : 'Erro'}:</strong> {resultado.text}
                </div>
              )}

              <div className="wa-iniciar-modal-actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={fecharModalIniciar}
                  disabled={enviando}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={enviando || !templateSelecionado}>
                  {enviando ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default WhatsApp
